if (!customElements.get('store-hours')) {
  customElements.define(
    'store-hours',
    class StoreHours extends HTMLElement {
      connectedCallback() {
        try {
          this.config = JSON.parse(this.querySelector('[data-store-hours-config]')?.textContent || '');
        } catch {
          return;
        }

        this.locale = this.config.locale || document.documentElement.lang || 'en-AU';
        this.timeZone = this.config.timeZone || 'Australia/Sydney';
        this.copy = this.config.copy || {};
        this.exceptions = this.parseExceptions(this.config.exceptions);
        this.events = new AbortController();

        document.addEventListener(
          'visibilitychange',
          () => {
            if (!document.hidden) this.update();
          },
          { signal: this.events.signal }
        );

        this.update();
      }

      disconnectedCallback() {
        window.clearTimeout(this.timer);
        this.events?.abort();
      }

      parseTime(value) {
        const match = String(value).trim().match(/^([01]\d|2[0-3]):([0-5]\d)$/);
        return match ? Number(match[1]) * 60 + Number(match[2]) : null;
      }

      parseSchedule(value) {
        const clean = String(value || '').trim().toLowerCase();
        if (!clean || clean === 'closed') return [];

        return clean
          .split(',')
          .map((interval) => {
            const [openValue, closeValue, ...extra] = interval.split('-').map((part) => part.trim());
            if (!openValue || !closeValue || extra.length) return null;
            const from = this.parseTime(openValue);
            let to = this.parseTime(closeValue);
            if (from == null || to == null || from === to) return null;
            if (to < from) to += 24 * 60;
            return { from, to };
          })
          .filter(Boolean)
          .sort((a, b) => a.from - b.from);
      }

      parseExceptions(value) {
        const exceptions = new Map();
        String(value || '')
          .split(/\r?\n/)
          .forEach((line) => {
            const [date, scheduleValue = '', ...noteParts] = line.split('|').map((part) => part.trim());
            if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
            const parsedDate = new Date(`${date}T00:00:00Z`);
            if (Number.isNaN(parsedDate.valueOf()) || parsedDate.toISOString().slice(0, 10) !== date) return;

            const closed = /^closed(?:\b|\s|,|—|-)/i.test(scheduleValue);
            const intervals = closed ? [] : this.parseSchedule(scheduleValue);
            if (!closed && !intervals.length) return;

            const inlineNote = closed ? scheduleValue.replace(/^closed[\s,:—-]*/i, '') : '';
            exceptions.set(date, {
              intervals,
              note: [...(inlineNote ? [inlineNote] : []), ...noteParts].join(' — '),
            });
          });
        return exceptions;
      }

      clock() {
        const options = {
          timeZone: this.timeZone,
          weekday: 'long',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        };

        let parts;
        try {
          parts = new Intl.DateTimeFormat('en-CA', options).formatToParts(new Date());
        } catch {
          options.timeZone = 'Australia/Sydney';
          parts = new Intl.DateTimeFormat('en-CA', options).formatToParts(new Date());
        }

        const get = (type) => parts.find((part) => part.type === type)?.value || '';
        return {
          date: `${get('year')}-${get('month')}-${get('day')}`,
          weekday: get('weekday').toLowerCase(),
          minutes: Number(get('hour')) * 60 + Number(get('minute')),
        };
      }

      dateAt(date, offset) {
        const value = new Date(`${date}T12:00:00Z`);
        value.setUTCDate(value.getUTCDate() + offset);
        return value.toISOString().slice(0, 10);
      }

      scheduleAt(clock, offset) {
        const order = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayIndex = order.indexOf(clock.weekday);
        const date = this.dateAt(clock.date, offset);
        const exception = this.exceptions.get(date);
        const day = order[(dayIndex + offset + 7) % 7];
        return {
          date,
          day,
          intervals: exception?.intervals ?? this.parseSchedule(this.config.days?.[day]),
          note: exception?.note || '',
          isException: Boolean(exception),
        };
      }

      pretty(minutes) {
        const value = minutes % (24 * 60);
        const date = new Date(Date.UTC(2020, 0, 1, Math.floor(value / 60), value % 60));
        return new Intl.DateTimeFormat(this.locale, {
          hour: 'numeric',
          minute: value % 60 ? '2-digit' : undefined,
          timeZone: 'UTC',
        }).format(date);
      }

      prettyDate(value) {
        const date = new Date(`${value}T12:00:00Z`);
        if (Number.isNaN(date.valueOf())) return value;
        return new Intl.DateTimeFormat(this.locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: 'UTC',
        }).format(date);
      }

      format(key, values = {}) {
        let message = this.copy[key] || '';
        Object.entries(values).forEach(([name, value]) => {
          message = message.replaceAll(`[[${name}]]`, value);
        });
        return message;
      }

      /**
       * The weekly table is really a rolling seven days: each weekday row is
       * the next time that day comes round. Resolving the date behind every
       * row is what lets a dated change land on the row a shopper is about to
       * rely on, rather than only in the list underneath.
       */
      upcomingSchedules(clock) {
        const schedules = new Map();
        for (let offset = 0; offset < 7; offset += 1) {
          const schedule = this.scheduleAt(clock, offset);
          if (!schedules.has(schedule.day)) schedules.set(schedule.day, schedule);
        }
        return schedules;
      }

      formatIntervals(intervals) {
        if (!intervals.length) return this.copy.closed || '';
        return intervals.map((interval) => `${this.pretty(interval.from)} – ${this.pretty(interval.to)}`).join(', ');
      }

      shortDate(value) {
        const date = new Date(`${value}T12:00:00Z`);
        if (Number.isNaN(date.valueOf())) return value;
        try {
          return new Intl.DateTimeFormat(this.locale, {
            day: 'numeric',
            month: 'short',
            timeZone: 'UTC',
          }).format(date);
        } catch {
          return value;
        }
      }

      /**
       * Replace one row's regular hours with a dated change, and put them back
       * when the date has passed. The untouched markup is kept so the restore
       * is exact; the time nodes inside it are re-localized by the pass that
       * follows this one in update().
       */
      applyException(row, schedule) {
        const cell = row.querySelector('[data-hours-value]');
        if (!cell) return;

        this.regularCells ||= new WeakMap();
        if (!this.regularCells.has(cell)) this.regularCells.set(cell, cell.innerHTML);

        if (!schedule?.isException) {
          if (row.classList.contains('is-exception')) {
            cell.innerHTML = this.regularCells.get(cell);
            row.classList.remove('is-exception');
          }
          return;
        }

        row.classList.add('is-exception');
        cell.textContent = '';

        // The qualifier leads so the hours keep the cell's right edge and stay
        // in line with every ordinary row. On a phone the wrapper reverses,
        // putting the hours back on top of the note.
        const wrapper = document.createElement('span');
        wrapper.className = 'hours__exception';

        const date = this.shortDate(schedule.date);
        const detail = schedule.note
          ? this.format('exceptionNote', { note: schedule.note, date })
          : this.format('exceptionOnly', { date });
        if (detail) {
          const qualifier = document.createElement('span');
          qualifier.className = 'hours__exception-note';
          qualifier.textContent = detail;
          wrapper.append(qualifier);
        }

        const value = document.createElement('span');
        value.className = 'hours__exception-value';
        value.textContent = this.formatIntervals(schedule.intervals);
        wrapper.append(value);

        cell.append(wrapper);
      }

      /**
       * Anything inside the next seven days is already spelled out on its own
       * row, so the list underneath is left holding only the changes the
       * weekly table has no way to reach. Empty it entirely and the box goes.
       */
      updateExceptionList(upcoming) {
        const shownInTable = new Set([...upcoming.values()].map((schedule) => schedule.date));

        document.querySelectorAll('[data-hours-date]').forEach((node) => {
          node.textContent = this.prettyDate(node.dataset.hoursDate);
          const item = node.closest?.('li');
          if (item) item.hidden = shownInTable.has(node.dataset.hoursDate);
        });

        document.querySelectorAll('[data-hours-exceptions]').forEach((box) => {
          const items = [...box.querySelectorAll('li')];
          box.hidden = items.length > 0 && items.every((item) => item.hidden);
        });
      }

      update() {
        window.clearTimeout(this.timer);
        const clock = this.clock();
        const today = this.scheduleAt(clock, 0);
        const yesterday = this.scheduleAt(clock, -1);
        const overnight = today.isException
          ? null
          : yesterday.intervals.find(
              (interval) => interval.to > 24 * 60 && clock.minutes + 24 * 60 >= interval.from && clock.minutes + 24 * 60 < interval.to
            );
        const current = today.intervals.find(
          (interval) => clock.minutes >= interval.from && clock.minutes < interval.to
        );
        const openInterval = current || overnight;
        let label;

        if (openInterval) {
          label = this.format('openUntil', { time: this.pretty(openInterval.to) });
        } else {
          const laterToday = today.intervals.find((interval) => interval.from > clock.minutes);
          if (laterToday) {
            label = this.format('opensToday', { time: this.pretty(laterToday.from) });
          } else if (today.isException && today.note) {
            label = this.format('closedToday', { note: today.note });
          } else {
            let next;
            for (let offset = 1; offset <= 7; offset += 1) {
              const schedule = this.scheduleAt(clock, offset);
              if (schedule.intervals.length) {
                next = { ...schedule, offset };
                break;
              }
            }

            if (!next) label = this.copy.closed || '';
            else if (next.offset === 1) {
              label = this.format('opensTomorrow', { time: this.pretty(next.intervals[0].from) });
            } else {
              label = this.format('opensDay', {
                time: this.pretty(next.intervals[0].from),
                day: this.copy.days?.[next.day] || next.day,
              });
            }
          }
        }

        if (!label) return;

        document.querySelectorAll('[data-open-status]').forEach((node) => {
          node.textContent = label;
          node.dataset.open = String(Boolean(openInterval));
        });

        const upcoming = this.upcomingSchedules(clock);
        document.querySelectorAll('[data-hours-day]').forEach((row) => {
          const isToday = row.dataset.hoursDay === clock.weekday;
          row.classList.toggle('is-today', isToday);
          row.querySelector('[data-today-label]')?.toggleAttribute('hidden', !isToday);
          this.applyException(row, upcoming.get(row.dataset.hoursDay));
        });

        document.querySelectorAll('[data-hours-time]').forEach((node) => {
          const minutes = this.parseTime(node.dataset.hoursTime);
          if (minutes != null) node.textContent = this.pretty(minutes);
        });

        this.updateExceptionList(upcoming);

        const millisecondsToNextMinute = (60 - new Date().getSeconds()) * 1000 + 50;
        this.timer = window.setTimeout(() => this.update(), millisecondsToNextMinute);
      }
    }
  );
}
